/**
 * Module #13/#36 — pure form logic for the consignment + tax profile editors.
 *
 * Extracted from the page components so the constraint-critical conversions +
 * validation are unit-tested without a DOM. These mirror the DB CHECK
 * constraints exactly:
 *   - consignment_profile_fees.consignment_fee_one_amount: EXACTLY ONE of
 *     amount_bps / amount_cents is non-null.
 *   - all *_bps columns: 0..10000 (i.e. percent 0..100).
 *   - fee_type / applies_to: fixed enums.
 */

import { dollarsToCents } from '@auctisync/shared';

export const FEE_TYPES = ['lot', 'listing', 'photo', 'no_sale', 'flat', 'other'] as const;
export type FeeType = (typeof FEE_TYPES)[number];

export const APPLIES_TO = ['per_lot', 'per_sold_lot', 'per_statement'] as const;
export type AppliesTo = (typeof APPLIES_TO)[number];

export const APPLIES_TO_LABEL: Record<AppliesTo, string> = {
  per_lot: 'per lot',
  per_sold_lot: 'per sold lot',
  per_statement: 'per statement',
};

export type FeeAmountMode = 'percent' | 'flat';

/** Editor-local draft for one consignment fee row. */
export interface FeeDraft {
  fee_type: FeeType;
  label: string;
  mode: FeeAmountMode;
  percent: string; // used when mode === 'percent'
  dollars: string; // used when mode === 'flat'
  applies_to: AppliesTo;
}

/** Editor-local draft for one tax rate row. */
export interface RateDraft {
  name: string;
  authority: string;
  percent: string; // rate %
  apply_to_bid: boolean;
  apply_to_premium: boolean;
  apply_to_expenses: boolean;
  is_active: boolean;
}

// ── percent ⇄ basis points ──────────────────────────────────────────────────

/** Percent string (e.g. "8.25") → basis points (825), or null if out of 0..100. */
export function pctToBps(pct: string): number | null {
  const n = Number(pct);
  if (pct.trim() === '' || !Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100);
}

export function bpsToPct(bps: number): string {
  return (bps / 100).toFixed(2);
}

export function newFee(): FeeDraft {
  return {
    fee_type: 'lot',
    label: '',
    mode: 'flat',
    percent: '',
    dollars: '',
    applies_to: 'per_sold_lot',
  };
}

export function newRate(): RateDraft {
  return {
    name: '',
    authority: '',
    percent: '',
    apply_to_bid: true,
    apply_to_premium: true,
    apply_to_expenses: false,
    is_active: true,
  };
}

// ── draft → DB row (the exactly-one-amount invariant lives here) ─────────────

export interface FeeRow {
  fee_type: FeeType;
  label: string;
  amount_bps: number | null;
  amount_cents: number | null;
  applies_to: AppliesTo;
  sort_order: number;
}

/** Convert a valid FeeDraft to a DB row. Sets EXACTLY ONE of amount_bps/amount_cents. */
export function feeToRow(fee: FeeDraft, sortOrder: number): FeeRow {
  const percentMode = fee.mode === 'percent';
  return {
    fee_type: fee.fee_type,
    label: fee.label.trim(),
    amount_bps: percentMode ? pctToBps(fee.percent) : null,
    amount_cents: percentMode ? null : dollarsToCents(Number(fee.dollars || '0')),
    applies_to: fee.applies_to,
    sort_order: sortOrder,
  };
}

export interface RateRow {
  name: string;
  authority: string | null;
  rate_bps: number;
  apply_to_bid: boolean;
  apply_to_premium: boolean;
  apply_to_expenses: boolean;
  is_active: boolean;
  sort_order: number;
}

/** Convert a valid RateDraft to a DB row. (Caller must validate first.) */
export function rateToRow(rate: RateDraft, sortOrder: number): RateRow {
  return {
    name: rate.name.trim(),
    authority: rate.authority.trim() === '' ? null : rate.authority.trim(),
    rate_bps: pctToBps(rate.percent) ?? 0,
    apply_to_bid: rate.apply_to_bid,
    apply_to_premium: rate.apply_to_premium,
    apply_to_expenses: rate.apply_to_expenses,
    is_active: rate.is_active,
    sort_order: sortOrder,
  };
}

// ── validation (returns user-facing error strings; empty = valid) ────────────

/** Map a Supabase error to a friendly message (the RPC can still race a 23505). */
export function profileSaveError(err: { code?: string; message: string }): string {
  if (err.code === '23505')
    return 'A profile with that name already exists. Pick a different name.';
  return err.message;
}

function nameTaken(name: string, existingNames: readonly string[]): boolean {
  const n = name.trim().toLowerCase();
  return existingNames.some((e) => e.trim().toLowerCase() === n);
}

export function validateConsignment(
  name: string,
  commissionPct: string,
  fees: FeeDraft[],
  existingNames: readonly string[] = [],
): string[] {
  const errs: string[] = [];
  if (name.trim() === '') errs.push('Profile name is required.');
  else if (nameTaken(name, existingNames)) errs.push('A profile with that name already exists.');
  if (pctToBps(commissionPct) === null) {
    errs.push('Commission must be a percent between 0 and 100.');
  }
  fees.forEach((f, i) => {
    const n = `Fee ${i + 1}`;
    if (f.label.trim() === '') errs.push(`${n}: a label is required.`);
    if (!FEE_TYPES.includes(f.fee_type)) errs.push(`${n}: invalid type.`);
    if (!APPLIES_TO.includes(f.applies_to)) errs.push(`${n}: invalid "applies to".`);
    if (f.mode === 'percent') {
      if (pctToBps(f.percent) === null) errs.push(`${n}: percent must be between 0 and 100.`);
    } else {
      const c = dollarsToCents(Number(f.dollars || '0'));
      if (!Number.isFinite(c) || c < 0) errs.push(`${n}: amount must be a positive dollar value.`);
    }
  });
  return errs;
}

export function validateTax(
  name: string,
  rates: RateDraft[],
  existingNames: readonly string[] = [],
): string[] {
  const errs: string[] = [];
  if (name.trim() === '') errs.push('Profile name is required.');
  else if (nameTaken(name, existingNames)) errs.push('A profile with that name already exists.');
  if (rates.length === 0) errs.push('Add at least one tax rate.');
  rates.forEach((r, i) => {
    const n = `Rate ${i + 1}`;
    if (r.name.trim() === '') errs.push(`${n}: a name is required.`);
    if (pctToBps(r.percent) === null) errs.push(`${n}: rate must be a percent between 0 and 100.`);
    if (!r.apply_to_bid && !r.apply_to_premium && !r.apply_to_expenses) {
      errs.push(`${n}: pick at least one of bid / premium / expenses to tax.`);
    }
  });
  return errs;
}
